/**
 * Headline Service
 *
 * Centralized service for building and rendering headline content.
 * Eliminates duplicate legacyData construction and headline rendering logic
 * across index.js.
 *
 * Usage:
 *   import { HeadlineService } from './services/HeadlineService.js';
 *
 *   const legacyData = HeadlineService.buildLegacyData(context);
 *   HeadlineService.renderPortfolioHeadline(elements, config, legacyData, calculatorResult);
 *   HeadlineService.renderClassHeadline(elements, config, legacyData, classKey, classData);
 */

import { HeadlineFormatter } from '../formatters/HeadlineFormatter.js';

const HeadlineService = {

  /**
   * Build the legacy data object used by HeadlineFormatter
   * This consolidates the duplicate legacyData construction across index.js
   *
   * @param {Object} context - Data context from getEquityData
   * @param {number} context.totalPaidInCapital - Total paid-in capital
   * @param {number} context.totalCurrentValue - Total current value
   * @param {number} context.totalDistributions - Total distributions
   * @param {Object} context.bases - Cost basis by class
   * @param {Object} context.latestValue - Latest value by class
   * @param {Object} context.distribs - Distributions by class
   * @param {Object} context.contributions - Contributions by class
   * @param {Object} context.returnsOfCapital - Returns of capital by class
   * @param {string[]} context.shareKeys - Array of share class keys
   * @param {string} context.labelPaidIn - Label for paid-in capital
   * @param {boolean} context.itsaFund - Whether this is a fund (UFL flag)
   * @param {string} context.valuationDate - Formatted valuation date string
   * @param {string} [context.currentClassValuationDate] - Formatted class valuation date
   * @returns {Object} - Legacy data object for HeadlineFormatter
   */
  buildLegacyData: function(context) {
    const {
      totalPaidInCapital = 0,
      totalCurrentValue = 0,
      totalDistributions = 0,
      bases = {},
      latestValue = {},
      distribs = {},
      contributions = {},
      returnsOfCapital = {},
      shareKeys = [],
      labelPaidIn = 'Paid-In Capital',
      itsaFund = false,
      valuationDate = '',
      currentClassValuationDate = ''
    } = context;

    return {
      totalPaidInCapital,
      totalCurrentValue,
      totalDistributions,
      netValue: totalCurrentValue - totalPaidInCapital,
      bases,
      latestValue,
      distribs,
      contributions,
      returnsOfCapital,
      shareKeys,
      labelPaidIn,
      itsaFund,
      valuationDate,
      currentClassValuationDate
    };
  },

  /**
   * Render a portfolio-level headline to DOM elements
   *
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.label - Main headline label element
   * @param {HTMLElement} elements.amount - Amount element (cleared for template headlines)
   * @param {HTMLElement} elements.subtitle - Subtitle/date element
   * @param {Object} config - Template configuration
   * @param {string} config.template - Headline template
   * @param {string} [config.subtitleTemplate] - Subtitle template
   * @param {Object} legacyData - Legacy data from buildLegacyData
   * @param {Object} [calculatorResult] - Calculator result if available
   * @returns {Object} - { handled: boolean, headline: { main, subtitle } }
   */
  renderPortfolioHeadline: function(elements, config, legacyData, calculatorResult) {
    const { label, amount, subtitle } = elements;

    if (!label) {
      return { handled: false, headline: null };
    }

    const headlineConfig = {
      template: config.template,
      subtitleTemplate: config.subtitleTemplate || ''
    };

    const headline = HeadlineFormatter.buildUnifiedHeadline(headlineConfig, {
      calculatorResult: calculatorResult,
      legacyData: legacyData
    });

    // Check if headline has meaningful value
    const calcTotalValue = calculatorResult?.totalValue || 0;
    const calcVestedValue = calculatorResult?.vestedValue || 0;
    const hasMeaningfulValue = legacyData.totalPaidInCapital > 0 ||
                               legacyData.totalCurrentValue > 0 ||
                               calcTotalValue > 0 ||
                               calcVestedValue > 0;

    if (headline.main && hasMeaningfulValue) {
      label.innerHTML = headline.main;
      if (amount) amount.innerHTML = '';
      if (subtitle) {
        subtitle.innerHTML = headline.subtitle
          ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.subtitle}</div>`
          : '';
      }
      return { handled: true, headline };
    }

    return { handled: false, headline };
  },

  /**
   * Render a class-level headline to DOM elements
   *
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.label - Main headline label element
   * @param {HTMLElement} elements.amount - Amount element (cleared for template headlines)
   * @param {HTMLElement} elements.subtitle - Subtitle/date element
   * @param {Object} config - Template configuration
   * @param {string} config.classTemplate - Class headline template
   * @param {string} [config.classSubtitleTemplate] - Class subtitle template
   * @param {Object} legacyData - Legacy data from buildLegacyData
   * @param {string} classKey - Current share class key
   * @param {Object} [classData] - Class data from calculator breakdown
   * @param {Object} [equityConfig] - Equity configuration for this class
   * @returns {Object} - { handled: boolean, headline: { main, subtitle } }
   */
  renderClassHeadline: function(elements, config, legacyData, classKey, classData, equityConfig) {
    const { label, amount, subtitle } = elements;

    if (!label) {
      return { handled: false, headline: null };
    }

    const headlineConfig = {
      classTemplate: config.classTemplate,
      classSubtitleTemplate: config.classSubtitleTemplate || ''
    };

    const equityName = equityConfig?.name || classKey;
    const currentLabel = equityConfig?.currentLabel || '';

    const headline = HeadlineFormatter.buildUnifiedClassHeadline(headlineConfig, {
      classData: classData,
      legacyData: legacyData,
      classKey: classKey,
      equityName: equityName,
      currentLabel: currentLabel
    });

    // Check if headline has meaningful value
    const classValue = classData?.value || legacyData.latestValue?.[classKey] || 0;
    const classBasis = legacyData.bases?.[classKey] || 0;
    const vestedValue = classData?.vestedValue || 0;

    if (headline.main && (classValue > 0 || classBasis > 0 || vestedValue > 0)) {
      label.innerHTML = headline.main;
      if (amount) amount.innerHTML = '';
      if (subtitle) {
        subtitle.innerHTML = headline.subtitle
          ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.subtitle}</div>`
          : '';
      }
      return { handled: true, headline };
    }

    return { handled: false, headline };
  },

  /**
   * Render headline to graph view elements (tradingData section)
   * Used when graph is displayed instead of landing page
   *
   * @param {Object} headline - { main, subtitle } from HeadlineFormatter
   */
  renderToGraphElements: function(headline) {
    const currentLabel = document.getElementById('currentLabel');
    const currentSymbol = document.getElementById('currentSymbol');
    const currentValue = document.getElementById('currentValue');
    const priceLabel = document.getElementById('priceLabel');
    const lastTraded = document.getElementById('lastTraded');
    const priceLabelReport = document.getElementById('priceLabelReport');

    if (currentLabel && currentSymbol && currentValue) {
      currentLabel.innerHTML = '';
      currentSymbol.innerHTML = headline.main;
      currentValue.innerHTML = '';
    }

    // Always update subtitle elements when template system is handling the headline
    // Hide the "Most recent Price" line if no subtitle, show subtitle if present
    if (priceLabel && lastTraded) {
      if (headline.subtitle) {
        priceLabel.innerHTML = '';
        lastTraded.innerHTML = headline.subtitle;
        if (priceLabelReport) priceLabelReport.classList.remove('hidden');
      } else {
        // Hide the price label line when there's no subtitle
        if (priceLabelReport) priceLabelReport.classList.add('hidden');
      }
    }
  }
};

export { HeadlineService };
